import * as React from 'react';
import { Typography, List, ListItem, ListItemText, Box, Stack, Button, Divider } from '@mui/material';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import InvoiceItemForm from '../forms/InvoiceItemForm';
import { useDeleteInvoiceItem } from '../../../hooks/invoice/useDeleteInvoiceItem';
import { numberToCurrencyFormat } from '../../../utils/numberToCurrencyFormat';
import InvoiceTransactionItemsForm from '../forms/InvoiceTransactionItemsForm';

export default function InvoiceItemsList({ invoice, onInvoiceItemSaveSuccess, onInvoiceItemDeleteSuccess }) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const grandTotal = invoice.items.reduce((sum, item) => sum + parseFloat(item.line_total), 0);

  const [itemFormOpen, setItemFormOpen] = React.useState(false);
  const [itemFormMode, setItemFormMode] = React.useState('create');
  const [selectedItem, setSelectedItem] = React.useState(null);

  const [transactionItemsFormOpen, setTransactionItemsFormOpen] = React.useState(false);

  const handleAddItem = () => {
    setSelectedItem(null);
    setItemFormMode('create');
    setItemFormOpen(true);
  };

  const handleAddFromTransactions = () => {
    setTransactionItemsFormOpen(true);
  };

  const handleListItemEdit = (item) => {
    setSelectedItem(item);
    setItemFormMode('edit');
    setItemFormOpen(true);
  };

  const onDeleteItemSuccess = () => {
    onInvoiceItemDeleteSuccess(invoice);
    showSnackbar(t('invoice.items.messages.delete_success'), "success");
  };

  const { remove } = useDeleteInvoiceItem(onDeleteItemSuccess);

  const handleListItemDelete = async (item) => {
    try {
      await remove(item.id);
    } catch (err) {
      showSnackbar(t('invoice.items.messages.delete_fail'), "error");
    }
  };
  
  return (
    <>
    <List>
    {invoice.items.map((item) => (
      <React.Fragment key={"item" + item.id}>
      <ListItem 
        sx={{ py: 1 }}
        alignItems="flex-start"
        secondaryAction={
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <VerticalHamburgerMenu
              options={[
                  { 
                      id: 1, 
                      name: 'Edit',
                      callback: () => handleListItemEdit(item)
                  },
                  { 
                    id: 2, 
                    name: 'Delete',
                    callback: () => handleListItemDelete(item),
                    confirmMessage: t('invoice.items.messages.delete_confirm', { name: item.name }),
                  }
              ]}
              />
          </Box>
        }
      >
        <ListItemText
          slotProps={{ 
            primary: { component: "div" }, 
            secondary: { component: "div" },  
          }}
          primary={
            <>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                maxWidth: '85%'
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Typography component="span" variant="body1">
                  {`${item.name}`}
                </Typography>
              </Stack>
            </Stack>
            </>
          }
          secondary={
            <>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  maxWidth: '85%',
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography component="span" variant="body2">
                  {`qty ${removeZeroDecimals(item.quantity)} x price ${numberToCurrencyFormat(item.unit_price)}`}
                </Typography>
                <Typography component="span" variant="body1">
                  {`${numberToCurrencyFormat(item.line_total)}`}
                </Typography>
              </Stack>
            </>
          }
        />
      </ListItem>
      <Divider />
      </React.Fragment>
    ))}
    </List>
    <Stack
      direction="row"
      spacing={1}
      sx={{
        mt: 2,
        maxWidth: '85%',
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography component="span" variant="body2">
        Total
      </Typography>
      <Typography component="span" variant="body1">
        {`${numberToCurrencyFormat(grandTotal.toFixed(2))}`}
      </Typography>
    </Stack>

    <Stack sx={{mt: 2}}spacing={1}>
    <Button onClick={handleAddItem} variant='outlined' fullWidth>
      Add Item
    </Button>
    <Button onClick={handleAddFromTransactions} variant='outlined' fullWidth>
      From Transactions
    </Button>
    </Stack>

    <InvoiceItemForm
      invoice={invoice}
      mode={itemFormMode}
      item={selectedItem}
      onInvoiceItemSaveSuccess={onInvoiceItemSaveSuccess}
      open={itemFormOpen}
      onClose={() => setItemFormOpen(false)}
    />

    <InvoiceTransactionItemsForm
      invoice={invoice}
      onInvoiceItemSaveSuccess={onInvoiceItemSaveSuccess}
      open={transactionItemsFormOpen}
      onClose={() => setTransactionItemsFormOpen(false)}
    />
    </>
  );
}